import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { DeviationCheckSearchRequest } from '../models/api/deviationCheckSearchRequest';
import { DeviationCheckSearchResponse } from '../models/api/deviationCheckRowDto';
import { DeviationCheckVariable } from '../models/enums/deviationCheckVariable.enum';
import { Capitalization } from '../models/enums/capitalization.enum';

@Injectable({
  providedIn: 'root'
})
export class DeviationCheckService {
  private http = inject(HttpClient);
  private apiUrl = `${environment.apiUrl}/tools/divcheck`;

  /**
   * GET /api/tools/divcheck/search
   * Paginated preview of the deviation report.
   */
  search(request: DeviationCheckSearchRequest): Observable<DeviationCheckSearchResponse> {
    return this.http.get<DeviationCheckSearchResponse>(`${this.apiUrl}/search`, {
      params: this.toParams(request)
    });
  }

  /**
   * GET /api/tools/divcheck/export
   * Exports the full deviation report as CSV.
   */
  exportCsv(request: DeviationCheckSearchRequest): Observable<HttpResponse<Blob>> {
    return this.http.get(`${this.apiUrl}/export`, {
      params: this.toParams(request),
      responseType: 'blob',
      observe: 'response'
    });
  }

  private toParams(request: DeviationCheckSearchRequest): HttpParams {
    let params = new HttpParams()
      .set('variable', request.variable.toString())
      .set('countryId', request.countryId);

    if (request.year !== undefined && request.year !== null) {
      params = params.set('year', request.year.toString());
    }
    if (request.capitalization !== undefined && request.capitalization !== null) {
      params = params.set('capitalization', request.capitalization.toString());
    }
    if (request.pageNumber !== undefined && request.pageNumber !== null) {
      params = params.set('pageNumber', request.pageNumber.toString());
    }
    if (request.pageSize !== undefined && request.pageSize !== null) {
      params = params.set('pageSize', request.pageSize.toString());
    }
    if (request.sortBy) {
      params = params.set('sortBy', request.sortBy);
    }
    if (request.ascending !== undefined && request.ascending !== null) {
      params = params.set('ascending', request.ascending.toString());
    }
    return params;
  }
}

/** Display label for a DeviationCheckVariable. */
export function deviationVariableLabel(v: DeviationCheckVariable): string {
  switch (v) {
    case DeviationCheckVariable.Audit: return 'Per Company - Audit';
    case DeviationCheckVariable.Board: return 'Per Company - Board & Committees';
    case DeviationCheckVariable.Ownership: return 'Per Company - Ownership';
    case DeviationCheckVariable.Compensation: return 'Per Company - Compensation';
    case DeviationCheckVariable.Family: return 'Per Company - Family';
    case DeviationCheckVariable.All: return 'Per Company - All';
    case DeviationCheckVariable.PerDirector: return 'Per Director';
    default: return '';
  }
}

/** Display label for a Capitalization (or empty when null). */
export function capitalizationLabel(c: Capitalization | null | undefined): string {
  if (c === null || c === undefined) return '';
  return Capitalization[c] ?? '';
}
